import React, { useState, useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import {
  AnimationMixer,
  InterpolateDiscrete,
  LoopOnce,
  LoopRepeat,
  MathUtils,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { mapLinear } from "three/src/math/MathUtils";
import { useGLTF } from "@react-three/drei";

// Module-level: tracks which characters have already played their intro (resets on page reload)
const introHasPlayedMap: Record<string, boolean> = {};

interface CharacterProps {
  index: number;
  characterData: any;
  questionData: any;
  setSelectedQuestion: any;
  modelPath: string;
  animPath: string;
  introAnimPath?: string;
  introAudioPath?: string;
  introReady?: boolean;
  onIntroStart?: () => void;
  onIntroEnd?: () => void;
  skipIntroRef?: React.MutableRefObject<(() => void) | null>;
  replayIntroRef?: React.MutableRefObject<(() => void) | null>;
  onCameraLerpChange?: (isLerping: boolean) => void;
}

export function Character({
  index,
  characterData,
  questionData,
  setSelectedQuestion,
  modelPath,
  animPath,
  introAnimPath,
  introAudioPath,
  introReady,
  onIntroStart,
  onIntroEnd,
  skipIntroRef,
  replayIntroRef,
  onCameraLerpChange,
}: CharacterProps) {
  const gltfModel = useGLTF(modelPath);
  const gltfAnim = useGLTF(animPath);
  const group = useRef();
  const [mixer, setMixer] = useState<AnimationMixer | null>(null);
  const [introKey, setIntroKey] = useState(0);
  const frameDuration = 1 / 12; // 12fps in seconds
  // One-frame blend keeps transitions stable without visibly smoothing stepped mouth poses.
  const transitionDuration = 1 / 60;

  useEffect(() => {
    let audio: HTMLAudioElement;
    let action: any;
    let mixer: AnimationMixer | null = null;
    let finishedHandler: any;
    let stopActionTimeout: number | undefined;

    if (gltfModel) {
      mixer = new AnimationMixer(gltfModel.scene);
      let idleAction: any = mixer.clipAction(gltfAnim.animations[0]);

      gltfModel.scene.traverse((child: any) => {
        if (child.isSkinnedMesh || child.isMesh) {
          child.frustumCulled = false;
        }
      });

      if (questionData) {
        // Play idle immediately so the mixer always has an active action.
        // Without this, the first newMixer.update() call resets all bones to
        // bind/rest pose (T-pose) before the question animation finishes loading,
        // producing a visible flash (especially noticeable on FishingRod in LakeBearbine).
        idleAction.setLoop(LoopRepeat);
        idleAction.play();

        const animationFile = questionData.animations.find(
          (q: any) => q.characterName === characterData.name,
        );

        // Load the animation file
        const loader = new GLTFLoader();
        loader.load(animationFile.file, (anim) => {
          // Play the animation and audio for the question
          if (mixer) {
            action = mixer.clipAction(anim.animations[0]);

            // Set the interpolation type of all keyframe tracks to InterpolateDiscrete
            action.getClip().tracks.forEach((track: any) => {
              track.setInterpolation(InterpolateDiscrete);
            });

            // Always restart from frame 0; clipAction can reuse cached action state.
            action.reset();
            action.enabled = true;
            action.setEffectiveWeight(1);
            action.setEffectiveTimeScale(1);
          }

          action.setLoop(LoopOnce, 1); // Set the loop mode to LoopOnce
          action.clampWhenFinished = true; // Set clampWhenFinished to true to pause the animation on the last frame

          audio = new Audio(questionData.audio);

          // Wait for audio to be loaded
          audio.addEventListener(
            "canplaythrough",
            () => {
              if (!action) return;

              const startQuestionAction = () => {
                action.reset();
                action.play();
                action.crossFadeFrom(idleAction, transitionDuration, false);
              };

              if (index === 0) {
                audio.play();
                setTimeout(() => {
                  startQuestionAction();
                }, 150);
              } else {
                startQuestionAction();
              }
            },
            { once: true },
          );

          audio.load(); // Explicitly trigger loading
        });

        // Listen to the 'finished' event on the mixer
        finishedHandler = () => {
          // Play the "idle" animation
          if (mixer) {
            action.crossFadeTo(idleAction, transitionDuration, false);
            idleAction.play();
          }

          // Stop the current action and audio
          if (stopActionTimeout) {
            window.clearTimeout(stopActionTimeout);
          }
          stopActionTimeout = window.setTimeout(() => {
            action.stop();
          }, transitionDuration * 1000);
          audio.pause();
          audio.currentTime = 0; // Optional: reset audio to the start

          setSelectedQuestion(null);
        };

        mixer.addEventListener("finished", finishedHandler);
      } else {
        // Play the idle animation continuously
        idleAction.setLoop(LoopRepeat);
        idleAction.play();
      }

      setMixer(mixer);
    }

    // Cleanup function
    return () => {
      if (stopActionTimeout) {
        window.clearTimeout(stopActionTimeout);
      }
      if (mixer) {
        mixer.removeEventListener("finished", finishedHandler);
      }
      if (audio) {
        audio.pause();
        audio.currentTime = 0; // Optional: reset audio to the start
      }
      if (action) {
        action.stop();
      }
    };
  }, [gltfModel, gltfAnim, questionData]);

  const lastFrameTimeRef = useRef(0);
  const accumulator = useRef(0);

  useEffect(() => {
    if (questionData) {
      lastFrameTimeRef.current = 0;
      accumulator.current = 0;
    }
  }, [questionData]);

  // Expose a replay function so the UI can restart the intro imperatively
  useEffect(() => {
    if (!replayIntroRef) return;
    replayIntroRef.current = () => {
      introHasPlayedMap[modelPath] = false;
      setIntroKey((k) => k + 1);
    };
    return () => {
      if (replayIntroRef) replayIntroRef.current = null;
    };
  }, [replayIntroRef, modelPath]);

  // Play intro animation once the camera has finished its mount transition
  useEffect(() => {
    if (
      !introReady ||
      !introAnimPath ||
      introHasPlayedMap[modelPath] ||
      !mixer
    ) {
      onCameraLerpChange?.(false);
      return;
    }

    let introAudio: HTMLAudioElement | undefined;
    let introAction: any;
    let introFinishedHandler: any;

    const loader = new GLTFLoader();
    loader.load(introAnimPath, (introGltf) => {
      if (!mixer) return;
      const idleAction = mixer.existingAction(gltfAnim.animations[0]);
      if (!idleAction) return;

      introAction = mixer.clipAction(introGltf.animations[0]);
      introAction.getClip().tracks.forEach((track: any) => {
        track.setInterpolation(InterpolateDiscrete);
      });
      introAction.setLoop(LoopOnce, 1);
      introAction.clampWhenFinished = true;

      introFinishedHandler = () => {
        introHasPlayedMap[modelPath] = true;
        onIntroEnd?.();
        if (skipIntroRef) skipIntroRef.current = null;
        if (mixer) {
          idleAction.reset();
          idleAction.setLoop(LoopRepeat, Infinity);
          idleAction.play();
          idleAction.crossFadeFrom(introAction, transitionDuration, false);
        }
        if (introAudio) {
          introAudio.pause();
          introAudio.currentTime = 0;
        }
      };
      mixer.addEventListener("finished", introFinishedHandler);

      if (skipIntroRef) {
        skipIntroRef.current = () => {
          introHasPlayedMap[modelPath] = true;
          onIntroEnd?.();
          if (skipIntroRef) skipIntroRef.current = null;
          if (mixer && introFinishedHandler) {
            mixer.removeEventListener("finished", introFinishedHandler);
          }
          if (introAudio) {
            introAudio.pause();
            introAudio.currentTime = 0;
          }
          if (introAction) introAction.stop();
          if (mixer && idleAction) {
            idleAction.reset();
            idleAction.setLoop(LoopRepeat, Infinity);
            idleAction.play();
          }
        };
      }

      if (introAudioPath) {
        introAudio = new Audio(introAudioPath);
        introAudio.addEventListener(
          "canplaythrough",
          () => {
            onIntroStart?.();
            introAction.reset();
            introAction.play();
            introAction.crossFadeFrom(idleAction, transitionDuration, false);
            introAudio!.play();
            onCameraLerpChange(false);
          },
          { once: true },
        );
        introAudio.load();
      } else {
        onIntroStart?.();
        introAction.reset();
        introAction.play();
        introAction.crossFadeFrom(idleAction, transitionDuration, false);
      }
    });

    return () => {
      if (skipIntroRef) skipIntroRef.current = null;
      if (mixer && introFinishedHandler) {
        mixer.removeEventListener("finished", introFinishedHandler);
      }
      if (introAudio) {
        introAudio.pause();
        introAudio.currentTime = 0;
      }
      if (introAction) {
        introAction.stop();
      }
    };
  }, [introReady, mixer, introKey]);

  useFrame((state, delta) => {
    if (mixer) {
      // Get the elapsed time since the application started in seconds
      const elapsedTime = state.clock.elapsedTime;

      // Calculate time since the last frame
      const delta = elapsedTime - lastFrameTimeRef.current;
      lastFrameTimeRef.current = elapsedTime;

      // Accumulate time
      accumulator.current += delta;

      while (accumulator.current >= frameDuration) {
        // Update the mixer by frameDuration each loop to avoid skipping frames on slow updates
        mixer.update(frameDuration);
        accumulator.current -= frameDuration;
      }
    }

    if (gltfModel && characterData.name !== "FishingRod") {
      let euler = gltfModel.nodes.phoneme_picker.rotation.z * MathUtils.RAD2DEG;
      let x =
        (mapLinear(Math.round(euler / 36) * 36, -180, 180, 0, 10) % 10) * 36;
      x = x === 324 ? 0 : x;

      let euler_emotion =
        gltfModel.nodes.emotion_picker.rotation.z * MathUtils.RAD2DEG;
      let w =
        (mapLinear(Math.round(euler_emotion / 90) * 90, -180, 180, 0, 4) % 4) *
        90;
      w = w > 90 ? 0 : w;

      const mouth = gltfModel.materials.spirit_bear_mouth;
      let uvX = (x % 180) / 180;

      let m = 0;
      if (x > 180) {
        m = 0.2;
      }
      let uvY = m + (w / 90) * 0.4;

      // @ts-ignore
      mouth.map.offset.set(uvX, uvY);

      // @ts-ignore
      // console.log(x, w);
    }

    if (gltfModel && characterData.name === "FishingRod") {
      // @ts-ignore
      if (gltfModel.materials.fishing_rod_ripple.map.offset.y >= 1) {
        // @ts-ignore
        gltfModel.materials.fishing_rod_ripple.map.offset.y = 0;
      } else {
        // @ts-ignore
        gltfModel.materials.fishing_rod_ripple.map.offset.y += 0.001;
      }
    }
  });

  return <primitive ref={group} object={gltfModel?.scene} />;
}
